/* eslint-disable react/prop-types */
import React, { useMemo } from 'react';
import { toTelFormat, toPlainFormat } from '../util/simple-phone-format';
import PropTypes from 'prop-types';
import { leadStatus } from '../constants/lead-status.enum';
import { jwtToken, jwtTokenLast } from './jwt-token';
import { commaOrAnyToStringArray } from '../util/comma-or-any-to-string-array';
import { processDisclaimer } from './process-disclaimer';

/**
 * The LeafGuard callback number to substitute for [[leaf_phone]]: the branch number resolved from
 * the visitor's ZIP on the /lead response once they have submitted, otherwise the tenant-wide
 * default baked into styleData at login. Empty string for non-LeafGuard orgs, which send neither.
 *
 * Falls back through jwtTokenLast for the same reason ConfirmNote and friends do — terminal pages
 * call resetJwtToken() on mount, and the confirmation screen is exactly the kind of page this
 * token targets, so without the fallback the number would blank out mid-render. resetJwtToken
 * snapshots every jwtToken key, so leafPhone rides along without any change there.
 *
 * The previous lead's number is only reachable via `allowLastLead`, which ONLY the terminal-page
 * getters below pass (ConfirmNote/ConfirmNoteTop/ConfirmBoxText/FollowUpBodyText). Two separate
 * ways it would otherwise leak into the next lead, both reachable in kiosk/call-center mode:
 *   - inside a live session, an unset leafPhone means THIS lead resolved to no branch and must
 *     fall to the current default, not the previous lead's number;
 *   - after resetJwtToken() the token is wiped, and that empty state is indistinguishable between
 *     "terminal page rendering the finished lead" and "entry page of the next lead, pre-auth" —
 *     route identity is the only thing that separates them, hence the explicit opt-in.
 * Reading the number and the default from the same generation also keeps the two coherent.
 */
export const getLeafPhone = (allowLastLead = false) => {
  if (jwtToken.data) {
    return jwtToken.leafPhone || jwtToken.data.styleData?.leafPhoneDefault || '';
  }
  if (allowLastLead) {
    return jwtTokenLast.leafPhone || jwtTokenLast.data?.styleData?.leafPhoneDefault || '';
  }
  // leafPhoneDefault is tenant-wide, so the snapshot's copy is still the right default to show
  // before the next handshake lands — only the per-lead branch number is withheld.
  return jwtTokenLast.data?.styleData?.leafPhoneDefault || '';
};

export const OptInCheck = () => {
  const data = jwtToken.data;
  return data?.styleData?.optInCheck || false;
};

export const Title1 = () => {
  const data = jwtToken.data;
  const leafPhone = getLeafPhone();
  const processedText = useMemo(
    () => processDisclaimer(data?.styleData?.text?.title1 || '', undefined, leafPhone),
    [data, leafPhone]
  );
  return <>{processedText}</>;
};

export const Subtitle1 = () => {
  const data = jwtToken.data;
  const leafPhone = getLeafPhone();
  const processedText = useMemo(
    () => processDisclaimer(data?.styleData?.text?.subtitle1 || '', undefined, leafPhone),
    [data, leafPhone]
  );
  return <>{processedText}</>;
};

export const googlePlaceId = () => {
  const data = jwtToken.data;
  return data?.styleData?.text?.googlePlaceId;
};

export const confirmNeeded = () => {
  const data = jwtToken.data || jwtTokenLast.data;
  return data?.styleData?.confirmNeeded && !data?.apptInfo?.confirmed;
};

export const smsConfirm = () => {
  const data = jwtToken.data || jwtTokenLast.data;
  return data?.styleData?.smsConfirm && !data?.apptInfo?.confirmed;
};

export const showCopyright = () => {
  const data = jwtToken.data || jwtTokenLast.data;
  return data?.styleData?.showCopyright;
};

export const showDetails = () => {
  const data = jwtToken.data;
  return data?.styleData?.showDetails;
};

export const showAllowBlankDetails = () => {
  const data = jwtToken.data;
  return data?.styleData?.showAllowBlankDetails;
};

export const showDetailsEnd = () => {
  const data = jwtToken.data;
  return data?.styleData?.showDetailsEnd;
};

export const showAllowBlankDetailsEnd = () => {
  const data = jwtToken.data;
  return data?.styleData?.showAllowBlankDetailsEnd;
};

export const showNotesPage = () => {
  const data = jwtToken.data;
  return data?.styleData?.showNotesPage;
};

export const NotesPageTitle = () => {
  const data = jwtToken.data;
  return data?.styleData?.text?.notesPageTitle || '';
};

export const NotesPagePlaceholder = () => {
  const data = jwtToken.data;
  return data?.styleData?.text?.notesPagePlaceholder || '';
};

export const thirtyMinSlices = () => {
  const data = jwtToken.data;
  return data?.styleData?.thirtyMinSlices;
};

export const adjacentSlots = () => {
  const data = jwtToken.data;
  return data?.styleData?.adjacentSlots;
};

export const useHorizontalLayout = () => {
  const data = jwtToken.data;
  return data?.styleData?.horizontal;
};

export const zipFirstMode = () => {
  return jwtToken?.data?.styleData?.zipFirstMode || false;
};

export const showHomeYear = () => {
  const data = jwtToken.data;
  return data?.styleData?.showHomeYear;
};

export const ShowB2b = () => {
  const data = jwtToken.data;
  return data?.styleData?.showB2b;
};

export const ShowAddressAutoComplete = () => {
  const data = jwtToken.data;
  return data?.styleData?.addressAutoFill;
};

export const LeadOnlyForm = () => {
  const data = jwtToken.data;
  const leadOnlyForm = data?.styleData?.leadOnlyForm;
  if (!leadOnlyForm) {
    return false;
  }
  const words = commaOrAnyToStringArray(data?.styleData?.leadOnlySearchWords);
  if (!words?.length) {
    return true;
  }
  const sourceString = (
    (jwtToken.parentUrl || '') +
    (window.location.href || '') +
    JSON.stringify(jwtToken.selectedSourceData)
  ).toLowerCase();
  for (const w of words) {
    if (sourceString.includes(w.toLowerCase())) {
      return true;
    }
  }
  return false; // search words provided, but no match found so allow appt
};

export const ShowStaff = () => {
  const data = jwtToken.data;
  return data?.styleData?.showStaff;
};

export const StaffOptional = () => {
  const data = jwtToken.data;
  return data?.styleData?.staffOptional;
};

export const showSingleStepForm = () => {
  const data = jwtToken.data;
  return data?.styleData?.showSingleStepForm;
};

export const productRadioButtons = () => {
  const data = jwtToken.data;
  return data?.styleData?.productRadioButtons;
};

export const ProductSelectorOnTop = () => {
  const data = jwtToken.data;
  return data?.styleData?.productSelectorOnTop || false;
};

export const PhoneBeforeEmail = () => {
  const data = jwtToken.data;
  return data?.styleData?.phoneBeforeEmail || false;
};

export const compactInputs = () => {
  const data = jwtToken.data;
  return data?.styleData?.compactInputs || data?.styleData?.floatingInputs || false;
};

export const inputIcons = () => {
  const data = jwtToken.data;
  return data?.styleData?.inputIcons;
};

export const DisclaimerAboveButton = () => {
  const data = jwtToken.data;
  return data?.styleData?.disclaimerAboveButton;
};

export const CallCenterMode = () => {
  const data = jwtToken.data;
  return data?.styleData?.callCenterMode;
};

export const TruvolvEnabled = () => {
  const data = jwtToken.data;
  return !!data?.styleData?.truvolvEnabled;
};

export const ShowSourceSelect = () => {
  const data = jwtToken.data;
  return data?.styleData?.showSourceSelect;
};

export const ShowQuestionSections = () => {
  const data = jwtToken.data;
  return !!data?.styleData?.text?.questionSections;
};

export const QuestionSectionsList = () => {
  const data = jwtToken.data;
  return data?.styleData?.text?.questionSections || [];
};

// True when any enabled question is tagged productMatching - its answer auto-selects a product,
// so the manual product picker is hidden rather than shown alongside it.
export const HasProductMatchingQuestion = () => {
  const data = jwtToken.data;
  const sections = data?.styleData?.text?.questionSections || [];
  return sections.some((s) => s.productMatching && s.enabled !== false);
};

export const QuestionSectionsLocation = () => {
  const data = jwtToken.data;
  const sections = data?.styleData?.text?.questionSections || [];
  const locations = [...new Set(sections.map((s) => s.location).filter(Boolean))];
  return locations;
};

export const Button1Text = () => {
  const data = jwtToken.data;
  const leafPhone = getLeafPhone();
  const processedText = useMemo(
    () => processDisclaimer(data?.styleData?.text?.button1 || 'Continue', undefined, leafPhone),
    [data, leafPhone]
  );
  return <>{processedText}</>;
};

export const ProductsPageTitle = () => {
  const data = jwtToken.data;
  const leafPhone = getLeafPhone();
  const processedText = useMemo(
    () => processDisclaimer(data?.styleData?.text?.productsPageTitle || '', undefined, leafPhone),
    [data, leafPhone]
  );
  return <>{processedText}</>;
};

export const ProductsPageSubtitle = () => {
  const data = jwtToken.data;
  const leafPhone = getLeafPhone();
  const processedText = useMemo(
    () => processDisclaimer(data?.styleData?.text?.productsPageSubtitle || '', undefined, leafPhone),
    [data, leafPhone]
  );
  return <>{processedText}</>;
};

export const ProductsPageButton = () => {
  const data = jwtToken.data;
  const leafPhone = getLeafPhone();
  const processedText = useMemo(
    () => processDisclaimer(data?.styleData?.text?.productsPageButton || 'Continue', undefined, leafPhone),
    [data, leafPhone]
  );
  return <>{processedText}</>;
};

export const InputsHeaderText = () => {
  const data = jwtToken.data;
  const leafPhone = getLeafPhone();
  const processedText = useMemo(
    () =>
      processDisclaimer(data?.styleData?.text?.inputsHeaderText || 'Provide Your Information', undefined, leafPhone),
    [data, leafPhone]
  );
  return <>{processedText}</>;
};

// eslint-disable-next-line react/prop-types
export const Button2Text = ({ status }) => {
  const data = jwtToken.data;
  const button2 = data?.styleData?.text?.button2 || '';
  const leafPhone = getLeafPhone();

  const processed = useMemo(() => processDisclaimer(button2, undefined, leafPhone), [button2, leafPhone]);

  if (button2) {
    return <>{processed}</>;
  }
  if (status === leadStatus.pendingNoApt) {
    return <>Submit</>;
  } else {
    return <>Select Day & Time</>;
  }
};

// Top-level flag (NOT under styleData.text) — mirror-off is entirely front-end's responsibility.
export const EnableNoAppointmentButton = () => {
  const data = jwtToken.data;
  return !!data?.styleData?.enableNoAppointmentButton;
};

// Label lives under styleData.text alongside button1/button2. Optional-with-default: falls back to
// a built-in when the admin leaves it blank.
export const NoAppointmentButtonText = () => {
  const data = jwtToken.data;
  const text = data?.styleData?.text?.noAppointmentButtonText || 'No appointment needed';
  const leafPhone = getLeafPhone();
  const processedText = useMemo(() => processDisclaimer(text, undefined, leafPhone), [text, leafPhone]);
  return <>{processedText}</>;
};

export const ExistingLeadGreeting1 = ({ firstName }) => {
  const data = jwtToken.data;
  let greeting = data?.styleData?.text?.existingLeadGreeting1 || '';
  if (greeting) {
    greeting = (greeting || '').replace(/\[\[first_name\]\]/g, firstName);
    return <>{greeting}</>;
  }
  return '';
};

export const ExistingLeadGreeting2 = ({ firstName }) => {
  const data = jwtToken.data;
  let greeting = data?.styleData?.text?.existingLeadGreeting2 || '';
  if (greeting) {
    greeting = (greeting || '').replace(/\[\[first_name\]\]/g, firstName);
    return <>{greeting}</>;
  }
  return '';
};

export const DisclaimerText = ({ index }) => {
  const data = jwtToken.data;
  const disclaimersConfig = DisclaimersConfig();
  const disclaimer = disclaimersConfig[index]?.text || data?.styleData?.text?.disclaimer;
  const leafPhone = getLeafPhone();

  const processedDisclaimer = useMemo(
    () => (disclaimer ? processDisclaimer(disclaimer, undefined, leafPhone) : null),
    [disclaimer, leafPhone]
  );

  if (processedDisclaimer) {
    return <span>{processedDisclaimer}</span>;
  }

  return (
    <>
      <span>
        You agree you would like to learn about our promotions &amp; you understand we use an automatic phone system and
        text messages to reach you. All calls and in-person meetings will be recorded for quality and training purposes.
        Your information will only be used by{' '}
      </span>
      <strong>
        <CompanyName />
      </strong>
    </>
  );
};

export const ConfirmNote = ({ defaultNote }) => {
  const data = jwtToken.data || jwtTokenLast.data;
  const confirmNote = data?.styleData?.text?.confirmNote;
  const firstName = jwtToken.userInfo?.firstName || jwtTokenLast.userInfo?.firstName || '';
  // Terminal page: resets on mount, so it reads this lead's number through the snapshot.
  const leafPhone = getLeafPhone(true);

  const processedConfirmNote = useMemo(
    () => (confirmNote ? processDisclaimer(confirmNote, firstName, leafPhone) : defaultNote),
    [confirmNote, firstName, leafPhone, defaultNote]
  );

  return <>{processedConfirmNote}</>;
};

export const ConfirmNoteTop = ({ defaultNote }) => {
  const data = jwtToken.data || jwtTokenLast.data;
  const confirmNoteTop = data?.styleData?.text?.confirmNoteTop;
  const firstName = jwtToken.userInfo?.firstName || jwtTokenLast.userInfo?.firstName || '';
  // Terminal page: resets on mount, so it reads this lead's number through the snapshot.
  const leafPhone = getLeafPhone(true);

  const processedConfirmNoteTop = useMemo(
    () => (confirmNoteTop ? processDisclaimer(confirmNoteTop, firstName, leafPhone) : defaultNote),
    [confirmNoteTop, firstName, leafPhone, defaultNote]
  );

  return <>{processedConfirmNoteTop}</>;
};

export const DisclaimerTextShort = ({ index }) => {
  const data = jwtToken.data;
  const disclaimersConfig = DisclaimersConfig();
  const disclaimer = disclaimersConfig[index]?.text || data?.styleData?.text?.disclaimer;
  const leafPhone = getLeafPhone();

  const processedDisclaimer = useMemo(
    () => (disclaimer ? processDisclaimer(disclaimer, undefined, leafPhone) : null),
    [disclaimer, leafPhone]
  );

  if (processedDisclaimer) {
    return <span>{processedDisclaimer}</span>;
  }

  return (
    <span>
      You consent to receive promotions via automated calls and texts from{' '}
      <b>
        <CompanyName />
      </b>{' '}
      which records interactions for quality. Your info is used solely by us.
    </span>
  );
};

export const BulletPoints1 = () => {
  const data = jwtToken.data;
  let bulletPoints1 = data?.styleData?.text?.bulletPoints1;
  bulletPoints1 = bulletPoints1 && bulletPoints1.length ? bulletPoints1 : [''];
  bulletPoints1 = bulletPoints1.map((b) => (b || '').trim()).filter((b) => !!b);
  const leafPhone = getLeafPhone();
  const bullets = bulletPoints1.map((b, i) => {
    return <li key={`b1-${i}`}>{processDisclaimer(b, undefined, leafPhone)}</li>;
  });
  return <>{bullets}</>;
};

export const CompanyName = () => {
  const data = jwtToken.data || jwtTokenLast.data;
  return <>{data?.styleData?.contact?.companyName || ''}</>;
};

export const Logo1 = () => {
  const data = jwtToken.data || jwtTokenLast.data;
  const logo1 = data?.styleData?.images?.logo1 || '';
  if (!logo1) {
    return <></>;
  }
  return <img src={logo1} alt="" className="logo"></img>;
};

export const DividerImage = () => {
  const data = jwtToken.data;
  const dividerImg = data?.styleData?.images?.dividerImage || '';
  if (!dividerImg) {
    return <></>;
  }
  return (
    <div className="divider-img">
      <img src={dividerImg} alt=""></img>
    </div>
  );
};

export const BottomImage = () => {
  const data = jwtToken.data || jwtTokenLast.data;
  const images = data?.styleData?.images || {};
  const bottomImages = images.bottomImages || [];
  if (bottomImages.length === 0) {
    return <></>;
  }
  return (
    <div className="bottom-img">
      {bottomImages.map((src, i) => (
        <img key={i} src={src} alt="" />
      ))}
    </div>
  );
};

export const ContactPhone = ({ plain }) => {
  const data = jwtToken.data || jwtTokenLast.data;
  const phone = data?.styleData?.contact?.phone || '';
  const telFormat = toTelFormat(phone);
  const regFormat = toPlainFormat(phone);
  const className = plain ? 'phone_plain' : 'phone_cal mt-3';
  return (
    <a className={className} href={`tel:${telFormat}`}>
      {regFormat}
    </a>
  );
};

export const ConfirmBoxText = () => {
  const data = jwtToken.data || jwtTokenLast.data;
  let confirmBoxText = (data?.styleData?.text?.confirmBoxText || '').trim();
  if (!confirmBoxText) {
    return '';
  }
  const phone = data?.styleData?.contact?.phone || '';
  confirmBoxText = (confirmBoxText || '').replace(/\[\[phone\]\]/g, `[[phone,${phone}]]`);
  // Terminal page: resets on mount, so it reads this lead's number through the snapshot.
  return processDisclaimer(confirmBoxText, undefined, getLeafPhone(true));
};

export const CountryAlpha3 = () => {
  const data = jwtToken.data;
  return data?.styleData?.contact?.countryAlpha3;
};

export const CountryAlpha2 = () => {
  const data = jwtToken.data;
  return data?.styleData?.contact?.countryAlpha2;
};

export const ProductList = () => {
  const data = jwtToken.data;
  return data?.styleData?.text?.products || [];
};

export const ProductImageList = () => {
  const data = jwtToken.data;
  return data?.styleData?.text?.productImages || [];
};

export const ProductsTitle = () => {
  const data = jwtToken.data;
  return data?.styleData?.text?.productsTitle || '';
};

export const DetailsTitlePlaceholder = () => {
  const data = jwtToken.data;
  return data?.styleData?.text?.detailsTitlePlaceholder || '';
};

export const DetailsPlaceholder = () => {
  const data = jwtToken.data;
  return data?.styleData?.text?.detailsPlaceholder || '';
};

export const DetailsEndTitlePlaceholder = () => {
  const data = jwtToken.data;
  return data?.styleData?.text?.detailsEndTitlePlaceholder || '';
};

export const DetailsEndPlaceholder = () => {
  const data = jwtToken.data;
  return data?.styleData?.text?.detailsEndPlaceholder || '';
};

export const SkipHearAbout = () => {
  const data = jwtToken.data;
  const set = !!data?.styleData?.text?.hearAboutSetSource && !!data?.styleData?.text?.hearAboutSourceType;
  if (!set) {
    return false;
  }
  const parentUrl = jwtToken.parentUrl || '';
  if (parentUrl.includes('?source=') || parentUrl.includes('&source=')) {
    return true;
  }
  if (parentUrl.includes('utm_source')) {
    return true;
  }
};

export const HearAboutList = () => {
  if (SkipHearAbout()) {
    return [];
  }
  const data = jwtToken.data;
  return (data?.styleData?.text?.hearAbout || []).filter((h) => !!(h || '').trim());
};

export const HearAboutTitle = () => {
  const data = jwtToken.data;
  return data?.styleData?.text?.hearAboutTitle || '';
};

export const MultiSelect = () => {
  const data = jwtToken.data;
  return data?.styleData?.text?.multiSelect || false;
};

export const ExistingSelect = () => {
  const data = jwtToken.data;
  const products = ProductList();
  if (products?.length > 0) {
    return data?.styleData?.text?.existingSelect || false;
  }
  return false;
};

export const EmailOptional = () => {
  const data = jwtToken.data;
  return data?.styleData?.emailOptional || false;
};

export const HideEndTime = () => {
  const data = jwtToken.data || jwtTokenLast.data;
  return data?.styleData?.hideEndTime || false;
};

export const DisableFontAutoSizing = () => {
  const data = jwtToken.data;
  return data?.styleData?.disableFontAutoSizing || false;
};

export const NamePlaceholder = () => {
  const data = jwtToken.data;
  return data?.styleData?.text?.namePlaceholder;
};

export const FirstNamePlaceholder = () => {
  const data = jwtToken.data;
  return data?.styleData?.text?.firstNamePlaceholder;
};

export const LastNamePlaceholder = () => {
  const data = jwtToken.data;
  return data?.styleData?.text?.lastNamePlaceholder;
};

export const AddressPlaceholder = () => {
  const data = jwtToken.data;
  return data?.styleData?.text?.addressPlaceholder;
};

export const EmailTitlePlaceholder = () => {
  const data = jwtToken.data;
  return data?.styleData?.text?.emailTitlePlaceholder || '';
};

export const EmailPlaceholder = () => {
  const data = jwtToken.data;
  // Returns the configured value unchanged, like PhonePlaceholder and ZipPlaceholder below. Baking the
  // default in here left callers unable to tell "the org set nothing" from "the org set this", which is
  // why the compact branches overrode a configured placeholder instead of falling back to one.
  return data?.styleData?.text?.emailPlaceholder || '';
};

export const PhonePlaceholder = () => {
  const data = jwtToken.data;
  return data?.styleData?.text?.phonePlaceholder;
};

export const ZipPlaceholder = () => {
  const data = jwtToken.data;
  return data?.styleData?.text?.zipPlaceholder;
};

export const MemberIdPlaceholder = () => {
  const data = jwtToken.data;
  return data?.styleData?.text?.memberIdPlaceholder;
};

// Title (label above input) getters. Empty string when unset so callers can fall back to the default label.
export const NameTitlePlaceholder = () => {
  const data = jwtToken.data;
  return data?.styleData?.text?.nameTitlePlaceholder || '';
};

export const FirstNameTitlePlaceholder = () => {
  const data = jwtToken.data;
  return data?.styleData?.text?.firstNameTitlePlaceholder || '';
};

export const LastNameTitlePlaceholder = () => {
  const data = jwtToken.data;
  return data?.styleData?.text?.lastNameTitlePlaceholder || '';
};

export const PhoneTitlePlaceholder = () => {
  const data = jwtToken.data;
  return data?.styleData?.text?.phoneTitlePlaceholder || '';
};

export const ZipTitlePlaceholder = () => {
  const data = jwtToken.data;
  return data?.styleData?.text?.zipTitlePlaceholder || '';
};

export const AddressTitlePlaceholder = () => {
  const data = jwtToken.data;
  return data?.styleData?.text?.addressTitlePlaceholder || '';
};

export const MemberIdTitlePlaceholder = () => {
  const data = jwtToken.data;
  return data?.styleData?.text?.memberIdTitlePlaceholder || '';
};

export const TimeSlotsNotFoundMessage = () => {
  const data = jwtToken.data;
  const text = data?.styleData?.text?.timeSlotsNotFoundMessage || '';
  const leafPhone = getLeafPhone();
  const processedText = useMemo(() => (text ? processDisclaimer(text, undefined, leafPhone) : null), [text, leafPhone]);
  return processedText ? <>{processedText}</> : null;
};

export const ZipCodeNotSupportedMessage = () => {
  const data = jwtToken.data;
  const text = data?.styleData?.text?.zipCodeNotSupportedMessage || '';
  const leafPhone = getLeafPhone();
  const processedText = useMemo(() => (text ? processDisclaimer(text, undefined, leafPhone) : null), [text, leafPhone]);
  return processedText ? <>{processedText}</> : null;
};

export const ShowMemberId = () => {
  const data = jwtToken.data;
  return data?.styleData?.showMemberId || false;
};

export const ShowLeadConfirm = () => {
  const data = jwtToken.data;
  return data?.styleData?.showLeadConfirm || false;
};

export const DisclaimerOnFocus = () => {
  const data = jwtToken.data;
  return data?.styleData?.disclaimerOnFocus || false;
};

export const FloatingInputs = () => {
  const data = jwtToken.data;
  return data?.styleData?.floatingInputs || false;
};

export const splitNameInputs = () => {
  const data = jwtToken.data;
  return data?.styleData?.splitName || false;
};

export const contactFirstPage = () => {
  const data = jwtToken.data;
  return data?.styleData?.contactFirstPage || false;
};

export const compactSlots = () => {
  const data = jwtToken.data;
  return data?.styleData?.compactSlots || false;
};

export const compactSlotsCalendarPopup = () => {
  const data = jwtToken.data;
  return data?.styleData?.compactSlotsCalendarPopup || false;
};

export const contactFirstWithZip = () => {
  const data = jwtToken.data;
  return data?.styleData?.contactFirstWithZip || false;
};

export const compactPhoneZip = () => {
  const data = jwtToken.data;
  return data?.styleData?.compactPhoneZip || false;
};

export const compactContactFields = () => {
  const data = jwtToken.data;
  return data?.styleData?.compactContactFields || false;
};

export const ProductsOnFirstPage = () => {
  const data = jwtToken.data;
  return data?.styleData?.productsOnFirstPage || false;
};

export const ProductsOnSecondPage = () => {
  const data = jwtToken.data;
  return data?.styleData?.productsOnSecondPage || false;
};

export const ProductsBeforeSlots = () => {
  const data = jwtToken.data;
  return data?.styleData?.productsBeforeSlots || false;
};

export const DetailsOnSecondPage = () => {
  const data = jwtToken.data;
  return data?.styleData?.detailsOnSecondPage || false;
};

export const FullNamePhoneSameLine = () => {
  const data = jwtToken.data;
  return data?.styleData?.fullNamePhoneSameLine || false;
};

export const ProductListGrid = () => {
  const data = jwtToken.data;
  return data?.styleData?.productListGrid || false;
};

export const HearAboutGrid = () => {
  const data = jwtToken.data;
  return data?.styleData?.hearAboutGrid || false;
};

/**
 * Style-wide default for a compact question's placement, now that each question section can carry
 * its own `afterStaffSelector`. Read directly off jwtToken by QuestionSectionsComp (alongside the
 * other compactQuestions* flags) and kept here for styles configured before the per-question
 * setting existed - their questions all inherit it.
 */
export const CompactQuestionsAfterStaffSelector = () => {
  const data = jwtToken.data;
  return data?.styleData?.compactQuestionsAfterStaffSelector || false;
};

export const HearAboutOnSecondPage = () => {
  const data = jwtToken.data;
  return data?.styleData?.hearAboutOnSecondPage || false;
};

export const ProductsTitleLabel = () => {
  const data = jwtToken.data;
  return data?.styleData?.text?.productsTitleLabel || '';
};

export const ShowDetailsAfterDialog = () => {
  const data = jwtToken.data;
  return data?.styleData?.showDetailsAfterDialog || false;
};

export const overrideProductsOnSecondPage = (newval) => {
  if (!jwtToken.data) {
    console.error('jwtToken.data is not initialized');
    return;
  }
  jwtToken.data.styleData = {
    ...(jwtToken.data.styleData || {}),
    productsOnSecondPage: newval
  };
};

export const TextBelowButton = () => {
  const data = jwtToken.data;
  const leafPhone = getLeafPhone();
  const processedText = useMemo(
    () => processDisclaimer(data?.styleData?.text?.textBelowButton || '', undefined, leafPhone),
    [data, leafPhone]
  );
  return <>{processedText}</>;
};

ContactPhone.propTypes = {
  plain: PropTypes.bool
};

export const ConfirmLeadBodyText = () => {
  const data = jwtToken.data;
  const text = data?.styleData?.text?.confirmLeadBodyText || '';
  const leafPhone = getLeafPhone();
  const processedText = useMemo(() => (text ? processDisclaimer(text, undefined, leafPhone) : null), [text, leafPhone]);
  return processedText ? <>{processedText}</> : null;
};

export const ConfirmLeadButtonText = () => {
  const data = jwtToken.data;
  return data?.styleData?.text?.confirmLeadButtonText || '';
};

export const FollowUpBodyText = () => {
  const data = jwtToken.data || jwtTokenLast.data;
  const text = data?.styleData?.text?.followUpBodyText || '';
  // Terminal page: resets on mount, so it reads this lead's number through the snapshot.
  const leafPhone = getLeafPhone(true);
  const processedText = useMemo(() => (text ? processDisclaimer(text, undefined, leafPhone) : null), [text, leafPhone]);
  return processedText ? <>{processedText}</> : null;
};

export const DisclaimersConfig = () => {
  const data = jwtToken.data;

  // Check for new format (array)
  const disclaimersArray = (data?.styleData?.text?.disclaimers || []).filter((x) => x && x.trim());
  if (disclaimersArray.length > 0) {
    const optInChecks = data?.styleData?.optInChecks || [];

    // Pair disclaimers with their corresponding optInChecks
    return disclaimersArray.map((disclaimer, index) => ({
      text: disclaimer,
      optIn: optInChecks[index] || null
    }));
  }

  let optInCheck = data?.styleData?.optInCheck ? { active: true, required: false } : undefined;
  const optInChecks = data?.styleData?.optInChecks || [];
  if (optInChecks.length > 0) {
    optInCheck = optInChecks[0];
  }

  // Check for old format - convert to array format
  if (data?.styleData?.text?.disclaimer) {
    return [
      {
        text: data.styleData.text.disclaimer,
        optIn: optInCheck || { active: false, required: false }
      }
    ];
  }

  return [
    {
      text: '',
      optIn: optInCheck || { active: false, required: false }
    }
  ];
};

export const getZipFirstTitle = () => {
  const text = jwtToken.data?.styleData?.text?.zipFirstTitle || '';
  return text || null;
};

export const getZipFirstSubtitle = () => {
  const text = jwtToken.data?.styleData?.text?.zipFirstSubtitle || '';
  return text || null;
};

export const getZipFirstButtonText = () => {
  const text = jwtToken.data?.styleData?.text?.zipFirstButtonText || '';
  return text || null;
};

export const PersistStaffSource = () => {
  return jwtToken.data?.styleData?.persistStaffSource || false;
};

// Per-form opt-in for typing a name that is missing from the CRM staff list. The backend
// ignores manualStaffName unless this is on, so the affordance stays hidden when it is off.
export const AllowManualStaffEntry = () => {
  return jwtToken.data?.styleData?.allowManualStaffEntry || false;
};
